import { useLanguage } from './useLanguage';
import { translations, SupportedLanguage } from '../locales';

type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

type TranslationKey = NestedKeyOf<typeof translations.en>;

/**
 * Custom hook for accessing translations with type safety and fallback logic.
 * 
 * @returns Object containing:
 *   - t: Translation function that accepts dot notation keys (e.g., 'nav.products')
 *   - language: Current language code
 * 
 * Fallback logic:
 *   1. Try to get translation for current language
 *   2. If missing, fall back to English translation
 *   3. If still missing, return the key itself (for debugging)
 */
export const useTranslation = () => {
  const { language } = useLanguage();

  /**
   * Get a nested value from an object using dot notation
   * @param obj - The object to traverse
   * @param path - Dot notation path (e.g., 'nav.products')
   * @returns The value at the path, or undefined if not found
   */
  const getNestedValue = (obj: any, path: string): string | undefined => {
    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return undefined;
      }
    }

    return typeof current === 'string' ? current : undefined;
  };

  /**
   * Translation function with fallback logic
   * @param key - Translation key in dot notation (e.g., 'nav.products')
   * @param params - Optional parameters for string interpolation (e.g., { max: 10 })
   * @returns Translated string
   */
  const t = (key: TranslationKey, params?: Record<string, string | number>): string => {
    // Try current language
    let value = getNestedValue(translations[language], key);

    // Fallback to English if not found and current language is not English
    if (value === undefined && language !== 'en') {
      value = getNestedValue(translations.en, key);
    }

    // If still not found, return the key itself for debugging
    if (value === undefined) {
      console.warn(`Missing translation key: ${key}`);
      return key;
    }

    // Handle parameter interpolation (e.g., "Only {max} kg available")
    if (params) {
      return Object.entries(params).reduce((str, [paramKey, paramValue]) => {
        return str.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(paramValue));
      }, value);
    }

    return value;
  };

  return { t, language };
};
